import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  deleteRecipe,
  saveRecipe,
} from '@/renderer/services/inventoryRecipeService';
import type { SaveRecipeRequest } from '@/renderer/types/inventory';
import { apiError, apiSuccess } from '@/renderer/util/notify';

export function useSaveRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: SaveRecipeRequest) => saveRecipe(request),
    onSuccess: (result, variables) => {
      if (!result.success) {
        apiError(result.error ?? 'Failed to save recipe');
        return;
      }
      apiSuccess(variables.id ? 'Recipe updated' : 'Recipe created');
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'recipes-list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'recipe-summary'],
      });
    },
    onError: (error: Error) => {
      apiError(error.message ?? 'Failed to save recipe');
    },
  });
}

export function useDeleteRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      recipeId,
    }: {
      companyId: number;
      recipeId: string;
    }) => deleteRecipe(companyId, recipeId),
    onSuccess: (result) => {
      if (!result.success) {
        apiError(result.error ?? 'Failed to delete recipe');
        return;
      }
      apiSuccess('Recipe deleted');
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'recipes-list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'recipe-summary'],
      });
    },
    onError: (error: Error) => {
      apiError(error.message ?? 'Failed to delete recipe');
    },
  });
}
